import { Component, signal, ViewChild, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TypeaheadComponent } from '../../../../parts/typeahead/typeahead.component';
import { TypeaheadItem } from '../../../../core/models/ui/typeahead-item.model';
import { MemberStatuses, OrganizationStatuses } from '../../../../core/enums/data.enums';
import { OrganizationsFilter } from '../../../../core/filters/organizations.filter';
import { UserListSortOptions } from '../../../../core/enums/sort-options.enums';
import { OrganizationService } from '../../../../core/services/organization.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { AuthorizationService } from '../../../../core/services/authorization/authorization.service';
import { AuthorizationStateService } from '../../../../core/services/authorization/authorization-state.service';
import { OrganizationModel } from '../../../../core/models/data/organization.model';
import { FaDuotoneIconComponent } from "@fortawesome/angular-fontawesome";
import { MemberPermission } from '../../../../core/enums/permissions.enums';

const SWITCHER_STAFF_TAKE = 100;
const SWITCHER_USER_TAKE = 10;

@Component({
  selector: 'ms-organization-switcher',
  imports: [TypeaheadComponent, FaDuotoneIconComponent],
  templateUrl: './organization-switcher.component.html',
  styleUrl: './organization-switcher.component.scss'
})
export class OrganizationSwitcherComponent {
  @ViewChild(TypeaheadComponent) typeaheadRef!: TypeaheadComponent;
  
  public organizationsTypeaheadList: TypeaheadItem[] = [];
  public organizationsList: OrganizationModel[] = [];
  public selectedOrganization = signal<TypeaheadItem | undefined>(undefined);
  public organizationsFilter: OrganizationsFilter = {};
  public isEditing = signal<boolean>(false);
  public isLoading = signal<boolean>(false);
  public isSearching = signal<boolean>(false);
  public isLoadingMore = signal<boolean>(false);
  public hasMore = signal<boolean>(false);
  public isStaff = signal<boolean>(false);
  // Whether the switcher should be interactive at all. Derived once from the initial
  // load's total count — NOT from live search results, whose length shrinks as the
  // staff user narrows a search (which would otherwise hide the typeahead mid-search).
  public hasMultipleOrgs = signal<boolean>(false);

  public orderBy: UserListSortOptions = UserListSortOptions.NameASC;
  public skip: number = 0;
  public take: number = SWITCHER_USER_TAKE;
  public MemberPermission = MemberPermission;

  private hasLoadedOnce = false;
  private currentSearchTerm = '';

  private router = inject(Router);
  private authorizationService = inject(AuthorizationService);
  private authorizationStateService = inject(AuthorizationStateService);

  constructor(
    private organizationService: OrganizationService,
    private userSessionService: UserSessionService
  ) {
    effect(() => {
      const user = this.userSessionService.activeUser();
      const authState = this.authorizationStateService.authorizationState();
      // Wait for both user + auth state so the staff branch in loadOrganizationsForSwitcher
      // can make the right call on first load — otherwise staff users would briefly see
      // only their own memberships before authState arrives. Flip hasLoadedOnce
      // synchronously so a second emission (user + authState arriving separately) can't
      // fire a duplicate initial load before the first await resolves.
      if (user && authState && !this.hasLoadedOnce) {
        this.hasLoadedOnce = true;
        void this.loadOrganizationsForSwitcher('', true);
      }
    });

    effect(() => {
      const activeOrg = this.userSessionService.activeOrganization();
      const currentSelection = this.selectedOrganization();

      if (activeOrg && activeOrg.organizationId !== currentSelection?.id) {
        this.selectedOrganization.set(this.toTypeaheadItem(activeOrg));
        this.isEditing.set(false);
      } else if (activeOrg && activeOrg.organizationId === currentSelection?.id && activeOrg.name !== currentSelection?.label) {
        // Active org's data changed in place (e.g. name edited in settings).
        // Refresh the typeahead label without flipping out of/into edit mode, and
        // sync the dropdown list too so the open-dropdown entry isn't stale.
        this.selectedOrganization.set(this.toTypeaheadItem(activeOrg));
        this.replaceOrganizationInLists(activeOrg);
      } else if (!activeOrg && !currentSelection) {
        this.isEditing.set(true);
      }
    });
  }

  public onOrganizationSelected(item: TypeaheadItem | undefined): void {
    if (!item?.id) return;

    const org = this.organizationsList.find(o => o.organizationId === item.id);
    if (!org) return;

    if (org.organizationId !== this.userSessionService.activeOrganization()?.organizationId) {
      this.selectedOrganization.set(item);
      this.userSessionService.activeOrganization.set(org);
      this.userSessionService.resetFilters();
      this.isEditing.set(false);
      this.router.navigate(['/dashboard']);

      // Refresh the selected org from the API so the switcher label and any downstream
      // consumers reflect current server state rather than whatever was cached in the
      // switcher's organizationsList (which may be stale from when the list was first loaded).
      void this.refreshActiveOrganization(org.organizationId);
    }
  }

  private async refreshActiveOrganization(organizationId: number): Promise<void> {
    try {
      const response = await this.organizationService.getOrganizationById(organizationId);
      if (!response.success || !response.data) return;

      const fresh = response.data;

      // Only propagate if this is still the active org — the user may have switched again
      // before our re-fetch resolved.
      if (this.userSessionService.activeOrganization()?.organizationId !== fresh.organizationId) return;

      this.userSessionService.activeOrganization.set(fresh);
      this.replaceOrganizationInLists(fresh);
    } catch (error) {
      console.error('Error refreshing active organization:', error);
    }
  }

  private replaceOrganizationInLists(updated: OrganizationModel): void {
    const idx = this.organizationsList.findIndex(o => o.organizationId === updated.organizationId);
    if (idx === -1) return;

    this.organizationsList[idx] = updated;
    this.organizationsTypeaheadList = this.mapToTypeaheadItems(this.organizationsList);
  }

  public enterEditMode(): void {
    this.isEditing.set(true);

    // Staff search is server-side, so the list currently holds whatever the last
    // search returned. Reload the unfiltered first page (via the search path so the
    // switcher UI stays mounted) so reopening always starts from a fresh, complete list.
    if (this.isStaff()) {
      void this.loadOrganizationsForSwitcher();
    }

    setTimeout(() => {
      this.typeaheadRef?.focusInput();
    }, 0);
  }

  public onSearchChanged(term: string): void {
    void this.loadOrganizationsForSwitcher(term);
  }

  public onLoadMore(): void {
    void this.loadMoreOrganizations();
  }

  public exitEditMode(): void {
    if (this.selectedOrganization()) {
      this.isEditing.set(false);
    }
  }

  public onTypeaheadBlurred(): void {
    this.exitEditMode();
  }

  public async loadOrganizationsForSwitcher(searchTerm: string = '', isInitial: boolean = false): Promise<void> {
    try {
      // The initial boot load owns the full-switcher spinner; searches and reopen
      // refreshes use the lighter in-dropdown "Searching…" state so the switcher UI
      // isn't torn down while the user is interacting with it.
      if (isInitial) {
        this.isLoading.set(true);
      } else {
        this.isSearching.set(true);
      }

      const user = this.userSessionService.activeUser();
      if (!user) {
        return;
      }

      const authState = this.authorizationStateService.authorizationState();
      const isStaff = authState ? this.authorizationService.isStaff(authState) : false;
      this.isStaff.set(isStaff);
      this.take = isStaff ? SWITCHER_STAFF_TAKE : SWITCHER_USER_TAKE;

      // Fresh page-zero load: reset paging and remember the term so infinite-scroll
      // load-more re-queries the same (possibly search-narrowed) result set.
      this.skip = 0;
      this.currentSearchTerm = searchTerm;

      const response = await this.fetchOrganizationsPage(searchTerm, this.skip);

      this.organizationsList = response.data?.data || [];
      this.hasMore.set(response.data?.hasNext ?? false);

      // Auto-select the first org and decide switcher interactivity only on the initial
      // load — later searches must not hijack the active org or toggle interactivity.
      if (isInitial) {
        this.hasMultipleOrgs.set((response.data?.total ?? this.organizationsList.length) > 1);

        // Zero-org detection requires a successful response — a failed call must not
        // flag the user as having no active organizations. Staff are excluded: their
        // filter isn't scoped to their own memberships.
        if (!isStaff && response.success && response.data) {
          if ((response.data.total ?? 0) === 0) {
            this.userSessionService.markNoActiveOrganizations();
          } else {
            this.userSessionService.hasNoActiveOrganizations.set(false);
          }
        }

        if (this.userSessionService.activeOrganization() === undefined && this.organizationsList.length > 0) {
          this.userSessionService.activeOrganization.set(this.organizationsList[0]);
        }
      }

      this.organizationsTypeaheadList = this.mapToTypeaheadItems(this.organizationsList);
    } catch (error) {
      console.error('Error loading organizations for switcher:', error);
    } finally {
      this.isLoading.set(false);
      this.isSearching.set(false);
    }
  }

  private async loadMoreOrganizations(): Promise<void> {
    if (this.isLoadingMore() || !this.hasMore()) {
      return;
    }

    try {
      this.isLoadingMore.set(true);

      const user = this.userSessionService.activeUser();
      if (!user) {
        return;
      }

      const nextSkip = this.skip + this.take;

      const response = await this.fetchOrganizationsPage(this.currentSearchTerm, nextSkip);

      // Only commit the advanced offset after a successful load — otherwise a failed
      // request would leave skip advanced and the next retry would skip a page.
      this.skip = nextSkip;

      const nextPage = response.data?.data || [];
      this.organizationsList = [...this.organizationsList, ...nextPage];
      this.hasMore.set(response.data?.hasNext ?? false);
      this.organizationsTypeaheadList = this.mapToTypeaheadItems(this.organizationsList);
    } catch (error) {
      console.error('Error loading more organizations for switcher:', error);
    } finally {
      this.isLoadingMore.set(false);
    }
  }

  private fetchOrganizationsPage(searchTerm: string, skip: number) {
    return this.organizationService.searchOrganizations(
      this.buildOrganizationsFilter(searchTerm),
      this.orderBy,
      skip,
      this.take
    );
  }

  private buildOrganizationsFilter(searchTerm: string): OrganizationsFilter {
    const user = this.userSessionService.activeUser();

    // Staff users can search and select every active org in the environment
    // (property managers, support, etc.) via server-side search-as-you-type;
    // non-staff are scoped to their own active memberships (client-side filtered).
    // Inactive and deleted orgs are excluded for every persona via the status filter.
    return this.isStaff()
      ? { statuses: [OrganizationStatuses.Active], searchTerm: searchTerm || undefined }
      : { userIds: user ? [user.userId] : [], statuses: [OrganizationStatuses.Active], memberStatuses: [MemberStatuses.Active] };
  }

  private mapToTypeaheadItems(organizations: OrganizationModel[]): TypeaheadItem[] {
    return organizations.map(org => this.toTypeaheadItem(org));
  }

  private toTypeaheadItem(organization: OrganizationModel): TypeaheadItem {
    return {
      id: organization.organizationId,
      label: organization.name,
      key: organization.name.replace(/\s+/g, '_').toLowerCase()
    };
  }

}
